import { Constants } from "config/constants";
import {
  convertTemplateToPdfAsync,
  uploadTemplateFileAsync,
} from "state/slices/templateFile";
import { handleFilesUpload } from "utils/filesUtils";
import {
  approveTransactionByDirectorAsync,
  createTransactionChangeAsync,
  editInitiatedTransactionChangeAsync,
} from "state/slices/initiateTransactionChange";
import store from "state/store";

export const createUploadFilePayload = (
  companyId,
  payload,
  officialId,
  fileName,
  callFor,
  trxId = null
) => {
  let modeType;
  let subModeType;
  let fileType;
  let status;
  if (
    payload.fileUploads?.ownConsentResolutionForDirectorApproval &&
    callFor === "directorApproval"
  ) {
    modeType = "pre-signed-documents";
    subModeType = "self-resolution-type";
    status = Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.REGISTRAR_REVIEW;
    fileType = Constants.Template_FILE_TYPES.CONSENT_TO_ACT;
  } else if (
    (payload.consentResolutionType.consentResolutionType ===
      "ownResolutionType" ||
      payload.directorResolution.directorResolution === "ownResolutionType") &&
    callFor === "directorApproval"
  ) {
    modeType = "not-signed-documents";
    subModeType = "self-resolution-type";
    status = Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.REGISTRAR_REVIEW;
    fileType = Constants.Template_FILE_TYPES.CONSENT_TO_ACT;
  } else if (
    payload.consentResolutionType.consentResolutionType ===
    "platformResolutionType" &&
    callFor === "directorApproval"
  ) {
    modeType = "not-signed-documents";
    subModeType = "platform-resolution-type";

    fileType = Constants.Template_FILE_TYPES.CONSENT_TO_ACT;
  } else if (
    payload.directorResolution.directorResolution ===
    "platformResolutionType" &&
    callFor === "directorApproval"
  ) {
    modeType = "not-signed-documents";
    subModeType = "platform-resolution-type";

    fileType = Constants.Template_FILE_TYPES.BOD_RESOLS_CHANGES;
  } else if (callFor === "courtOrder") {
    modeType = "pre-signed-documents";
    subModeType = "self-resolution-type";
    status = Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.REGISTRAR_REVIEW;
    fileType = Constants.Template_FILE_TYPES.COURT_ORDER;
  } else if (callFor === "creditorsResolution") {
    modeType = "pre-signed-documents";
    subModeType = "self-resolution-type";
    status = Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.REGISTRAR_REVIEW;
    fileType = Constants.Template_FILE_TYPES.CREDITORS_RESOLUTION;
  } else if (callFor === "gazetteNotice") {
    modeType = "pre-signed-documents";
    subModeType = "self-resolution-type";
    status = Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.REGISTRAR_REVIEW;
    fileType = Constants.Template_FILE_TYPES.GAZETTE_NOTICE;
  } else if (
    payload.fileUploads?.ownConsentResolutionForShareholderApproval &&
    callFor === "shareholderApproval"
  ) {
    modeType = "pre-signed-documents";
    subModeType = "self-resolution-type";
    status = Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.SHAREHOLDER_APPROVAL;
    fileType = Constants.Template_FILE_TYPES.SHAREHOLDER_APPROVAL;
  } else if (
    (payload.consentResolutionType.consentResolutionType ===
      "ownResolutionType" ||
      payload.shareholderResolution.shareholderResolution ===
      "ownResolutionType") &&
    callFor === "shareholderApproval"
  ) {
    modeType = "not-signed-documents";
    subModeType = "self-resolution-type";
    status = Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.SHAREHOLDER_APPROVAL;
    fileType = Constants.Template_FILE_TYPES.SHAREHOLDER_APPROVAL;
  } else if (
    (payload.consentResolutionType.consentResolutionType ===
      "platformResolutionType" ||
      payload.shareholderResolution.shareholderResolution ===
      "platformResolutionType") &&
    callFor === "shareholderApproval"
  ) {
    modeType = "not-signed-documents";
    subModeType = "platform-resolution-type";
    status = Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.SHAREHOLDER_APPROVAL;
    fileType = Constants.Template_FILE_TYPES.SHAREHOLDER_APPROVAL;
  }
  const payloadObj = {
    company: companyId,
    fileType,
    status,
    fileName: fileName,
    modeType: modeType,
    subModeType: subModeType,
  };
  if (trxId) {
    return {
      ...payloadObj,
      transaction: trxId,
    };
  } else {
    return {
      ...payloadObj,
      official: officialId,
    };
  }
};

// --- File Upload and PDF Conversion ---
async function uploadAndConvertDocument({
  companyId,
  formDetails,
  fileName,
  callFor,
  trxId,
}) {
  const uploadFilePayload = createUploadFilePayload(
    companyId,
    formDetails,
    null,
    fileName,
    callFor,
    trxId
  );
  const fileUploadResponse = await store.dispatch(
    uploadTemplateFileAsync({ data: uploadFilePayload })
  );
  let pdfFileName = "";
  if (
    fileUploadResponse.payload.document?.modeType === "not-signed-documents"
  ) {
    const convertToPdfResponse = await store.dispatch(
      convertTemplateToPdfAsync({ data: fileUploadResponse.payload.document })
    );
    pdfFileName = convertToPdfResponse.payload.fileName;
  }
  return {
    documentId: fileUploadResponse.payload.document._id,
    pdfFileName,
    document: fileUploadResponse.payload.document,
  };
}

/** Helper function to get next status for a transaction */
const getNextTransactionStatus = (currentStatus) => {
  switch (currentStatus) {
    case Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.DRAFT:
      return Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.REGISTRAR_REVIEW;

    case Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.REGISTRAR_CS_CORRECTIONS:
      return Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.REGISTRAR_CS_REVIEW_II;

    case Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.REGISTRAR_DR_CORRECTIONS:
      return Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.REGISTRAR_DR_REVIEW_II;

    case Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.REGISTRAR_SH_CORRECTIONS:
      return Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.REGISTRAR_SH_REVIEW_II;

    case Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.ADMIN_CORRECTIONS:
      return Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.ADMIN_REVIEW_II;

    default:
      return Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.REGISTRAR_REVIEW;
  }
};

// --- Approval Config Builder ---
function buildApprovalConfigs(formDetails, uploadedFiles) {
  let status = Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.DRAFT;
  if (
    !formDetails?.transactionType ||
    formDetails?.transactionType === Constants.TRANSACTION_TYPES.AUTOMATIC
  ) {
    status = getNextTransactionStatus(formDetails.status);
  }
  const getDirectorFileName = () => {
    if (
      formDetails.director_approval &&
      formDetails.director_approval.documentsAreSigned === "true"
    ) {
      return uploadedFiles.ownConsentResolutionForDirectorApproval;
    }
    if (
      formDetails.consentResolutionType.consentResolutionType ===
      "ownResolutionType" ||
      formDetails.directorResolution.directorResolution === "ownResolutionType"
    ) {
      return uploadedFiles.ownStatutoryDeclarationTemplate;
    }
    return (
      formDetails.consentResolutionType.consentResolutionDocumentName ||
      formDetails.directorResolution.consentResolutionDocumentName
    );
  };

  const getShareholderFileName = () => {
    if (
      formDetails.shareholder_approval &&
      formDetails.shareholder_approval.documentsAreSigned === "true"
    ) {
      return uploadedFiles.ownConsentResolutionForShareholderApproval;
    }
    if (
      formDetails.consentResolutionType.consentResolutionType ===
      "ownResolutionType" ||
      formDetails.shareholderResolution.shareholderResolution ===
      "ownResolutionType"
    ) {
      return uploadedFiles.ownConsentResolutionForShareholderApproval;
    }
    return (
      formDetails.consentResolutionType.consentResolutionDocumentName ||
      formDetails.shareholderResolution.consentResolutionDocumentName
    );
  };

  return [
    {
      enabled: formDetails.court_order_appointment,
      approvalType: "courtOrder",
      fileName: uploadedFiles
        ? uploadedFiles.ownConsentResolutionForCourtOrderApproval
        : null,
      callFor: "courtOrder",
      docsKey: "courtOrderApprovalDocs",
      pdfKey: "courtOrderApprovalPdf",
      status,
    },
    {
      enabled: formDetails.gazette_approval,
      approvalType: "gazetteNotice",
      fileName: uploadedFiles
        ? uploadedFiles.ownConsentResolutionForGazetteApproval
        : null,
      callFor: "gazetteNotice",
      docsKey: "gazetteApprovalDocs",
      pdfKey: "gazetteApprovalPdf",
      status,
    },
    {
      enabled: formDetails.director_approval,
      approvalType: "boardApproval",
      fileName: getDirectorFileName(),
      callFor: "directorApproval",
      docsKey: "directorApprovalDocs",
      pdfKey: "directorApprovalPdf",
      status,
    },
    {
      enabled: formDetails.shareholder_approval,
      approvalType: "shareholderApproval",
      fileName: getShareholderFileName(),
      callFor: "shareholderApproval",
      docsKey: "shareholderApprovalDocs",
      pdfKey: "shareholderApprovalPdf",
      status,
    },
    {
      enabled: formDetails.creditors_appointment,
      approvalType: "creditorsResolution",
      fileName: uploadedFiles
        ? uploadedFiles.ownConsentResolutionForCreditorsApproval
        : null,
      callFor: "creditorsResolution",
      docsKey: "creditorApprovalDocs",
      pdfKey: "creditorApprovalPdf",
      status,
    },
  ];
}

// --- Single Approval Processing ---
async function processApprovalConfig({
  config,
  formDetails,
  companyId,
  uploadedFilesResponse,
}) {
  if (!config.enabled) return;
  const transactions = formDetails.transactions.filter((transaction) => {
    if (
      (transaction.approvalType ===
        Constants.TRANSACTION_APPROVAL_TYPES.BOARD_SHAREHOLDER_APPROVAL &&
        config.approvalType ===
        Constants.TRANSACTION_APPROVAL_TYPES.BOARD_APPROVAL) ||
      (transaction.approvalType ===
        Constants.TRANSACTION_APPROVAL_TYPES.BOARD_SHAREHOLDER_APPROVAL &&
        config.approvalType ===
        Constants.TRANSACTION_APPROVAL_TYPES.SHAREHOLDER_APPROVAL) ||
      (transaction.approvalType ===
        Constants.TRANSACTION_APPROVAL_TYPES.BOARD_CREDITORS_APPROVAL &&
        config.approvalType ===
        Constants.TRANSACTION_APPROVAL_TYPES.BOARD_APPROVAL) ||
      (transaction.approvalType ===
        Constants.TRANSACTION_APPROVAL_TYPES.BOARD_CREDITORS_APPROVAL &&
        config.approvalType ===
        Constants.TRANSACTION_APPROVAL_TYPES.CREDITORS_RESOLUTION) ||
      (transaction.approvalType ===
        Constants.TRANSACTION_APPROVAL_TYPES.BOARD_COURT_APPROVAL &&
        config.approvalType ===
        Constants.TRANSACTION_APPROVAL_TYPES.BOARD_APPROVAL) ||
      (transaction.approvalType ===
        Constants.TRANSACTION_APPROVAL_TYPES.BOARD_COURT_APPROVAL &&
        config.approvalType ===
        Constants.TRANSACTION_APPROVAL_TYPES.COURT_ORDER)
    ) {
      return transaction;
    } else {
      return transaction.approvalType === config.approvalType;
    }
  });

  if (!transactions?.length) return;
  const { documentId, pdfFileName } = await uploadAndConvertDocument({
    companyId,
    formDetails,
    fileName: config.fileName,
    callFor: config.callFor,
    trxId: transactions[0]._id,
  });
  let responseObj = {
    approvalDocs: {
      [config.docsKey]: {
        templateFile: documentId,
        convertedPdf: pdfFileName,
      },
    },
    trxRef: transactions.map((data) => data.trxRef),
    status: config.status,
    changeDocumentSignature: formDetails.changeDocumentSignature,
  };
  uploadedFilesResponse.push(responseObj);
}

// --- Main Handler ---
export const handleCompanyMaintenanceFormData = async (
  type,
  formDetails,
  companyId,
  officialId = "",
  uploadFileName = "",
  trxIds = [],
  selectedTab = ""
) => {
  if (type === "new-request") {
    return await store.dispatch(
      createTransactionChangeAsync({
        companyId: companyId,
        data: formDetails,
      })
    );
  }

  if (type === "existing-request") {
    return await store.dispatch(
      editInitiatedTransactionChangeAsync({
        companyId: formDetails.companyId,
        formData: formDetails,
      })
    );
  }

  if (type === "director-approval") {
    const uploadedFiles = await handleFilesUpload(
      formDetails.fileUploads,
      false
    );

    let uploadedFilesResponse = [];
    const approvalConfigs = buildApprovalConfigs(formDetails, uploadedFiles);

    for (const config of approvalConfigs) {
      await processApprovalConfig({
        config,
        formDetails,
        companyId: formDetails.companyId,
        uploadedFilesResponse,
      });
    }
    const boardShareholderTransactions = formDetails.transactions.filter(
      (trx) =>
        trx.approvalType ===
        Constants.TRANSACTION_APPROVAL_TYPES.BOARD_SHAREHOLDER_APPROVAL
    );

    const boardCourtTransactions = formDetails.transactions.filter(
      (trx) =>
        trx.approvalType ===
        Constants.TRANSACTION_APPROVAL_TYPES.BOARD_COURT_APPROVAL
    );

    const boardCreditorsTransactions = formDetails.transactions.filter(
      (trx) =>
        trx.approvalType ===
        Constants.TRANSACTION_APPROVAL_TYPES.BOARD_CREDITORS_APPROVAL
    );

    // const mappedTrxRef = boardShareholderTransactions.map((trx) => trx?.trxRef);
    // const boardShareholderFileResponse = uploadedFilesResponse.filter((res) =>
    //   res.trxRef.includes(...mappedTrxRef)
    // );

    // let newRes = {};
    // boardShareholderFileResponse.forEach((res) => {
    //   newRes.approvalDocs = {
    //     ...newRes?.approvalDocs,
    //     [Object.keys(res.approvalDocs)]:
    //       res.approvalDocs[Object.keys(res.approvalDocs)],
    //   };
    // });
    // newRes.trxRef = mappedTrxRef;
    // uploadedFilesResponse.push(newRes);

    // await Promise.all(
    //   uploadedFilesResponse.map(async (data) => {
    //     await store.dispatch(
    //       approveTransactionByDirectorAsync({
    //         companyId: formDetails.companyId,
    //         data,
    //       })
    //     );
    //   })
    // );

    // Helper function to process transactions
    const processTransactions = (transactions, uploadedFiles) => {
      const mappedTrxRef = transactions.map((trx) => trx?.trxRef);
      const fileResponse = uploadedFiles.filter((res) =>
        res.trxRef.includes(...mappedTrxRef)
      );

      let result = {};
      fileResponse.forEach((res) => {
        result.approvalDocs = {
          ...result?.approvalDocs,
          [Object.keys(res.approvalDocs)]:
            res.approvalDocs[Object.keys(res.approvalDocs)],
        };
      });
      result.trxRef = mappedTrxRef;

      return Object.keys(result).length > 0 ? result : null;
    };

    // Process all transaction types
    let boardShareholderRes;
    if (boardShareholderTransactions.length > 0) {
      boardShareholderRes = processTransactions(
        boardShareholderTransactions,
        uploadedFilesResponse
      );
    }

    let boardCourtRes;
    if (boardCourtTransactions.length > 0) {
      boardCourtRes = processTransactions(
        boardCourtTransactions,
        uploadedFilesResponse
      );
    }

    let boardCreditorsRes;
    if (boardCreditorsTransactions.length > 0) {
      boardCreditorsRes = processTransactions(
        boardCreditorsTransactions,
        uploadedFilesResponse
      );
    }

    // Combine valid responses
    const allResponses = [
      boardShareholderRes,
      boardCourtRes,
      boardCreditorsRes,
    ].filter(Boolean);

    // Add to uploadedFilesResponse and dispatch
    uploadedFilesResponse.push(...allResponses);

    await Promise.all(
      uploadedFilesResponse.map(async (data) => {
        await store.dispatch(
          approveTransactionByDirectorAsync({
            companyId: formDetails.companyId,
            data,
          })
        );
      })
    );
  }
};
